"use client";

import { MdOutlineAdminPanelSettings } from "react-icons/md";
import { CiCircleQuestion, CiLogout, CiSettings } from "react-icons/ci";
import Link from "next/link";

import { SimpleTooltip } from "@/components/ui/tooltip";
import {
  NAV_INK_FOCUS_RING,
  sheetRowClass,
} from "@/components/nav/nav-styles";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { getInitials } from "@/components/ui/UserAvatar";
import { hardSignOut } from "@/lib/auth/clientSignOut";
import { cn } from "@/lib/utils";

const ICON = "h-5 w-5 shrink-0 text-gray-400";

/**
 * The signed-in person's menu behind their avatar: settings, help, the
 * superUser console when they have one, and sign out. On the rail it opens to
 * the right; in the mobile header it drops down. `ink` seats the trigger on
 * the navy rail: a white tint behind the avatar, and initials in navy on
 * white so they do not vanish into it.
 */
export default function AccountMenu({ user, side = "right", ink = false }) {
  if (!user) return null;

  const items = [
    user.workspaceId && {
      href: "/settings",
      label: "Settings",
      Icon: CiSettings,
    },
    { href: "/help", label: "Help Center", Icon: CiCircleQuestion },
    user.superUser &&
      !user.isImpersonated && {
        href: "/admin/superUser",
        label: "SuperUser console",
        Icon: MdOutlineAdminPanelSettings,
      },
  ].filter(Boolean);

  return (
    <Popover>
      <SimpleTooltip content="Account" side={side}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Account menu"
            className={cn(
              "flex h-11 w-11 items-center justify-center rounded-lg transition-colors",
              ink
                ? cn("hover:bg-white/5 data-[state=open]:bg-white/10", NAV_INK_FOCUS_RING)
                : "hover:bg-stone-200/50 data-[state=open]:bg-stone-200/50",
            )}
          >
            <Avatar className="h-8 w-8">
              <AvatarImage src={user.image || undefined} alt="" />
              <AvatarFallback
                className={cn(
                  "text-xs font-semibold",
                  ink ? "bg-white text-navy-900" : "bg-gray-700 text-white",
                )}
              >
                {getInitials(user.name, user.email)}
              </AvatarFallback>
            </Avatar>
          </button>
        </PopoverTrigger>
      </SimpleTooltip>
      <PopoverContent
        side={side}
        align="end"
        sideOffset={8}
        className="w-64 rounded-xl border-stone-200/80 p-2"
      >
        <div className="px-3 pb-2 pt-1">
          <div className="truncate text-sm font-semibold text-gray-900">
            {user.name || user.email}
          </div>
          {user.name && user.email && (
            <div className="truncate text-xs text-gray-500">{user.email}</div>
          )}
        </div>
        <div className="border-t border-gray-100 pt-1">
          {items.map(({ href, label, Icon }) => (
            <Link key={href} href={href} className={sheetRowClass(false)}>
              <Icon className={ICON} />
              {label}
            </Link>
          ))}
          <button
            type="button"
            onClick={() => hardSignOut("/")}
            className={`${sheetRowClass(false)} w-full`}
          >
            <CiLogout className={ICON} />
            Sign out
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
